/**
 * Safe localStorage operations with error handling
 */

/**
 * Safely get an item from localStorage
 * @param key - The localStorage key
 * @returns The stored value or null if not found or error occurred
 */
export const getFromLocalStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`Failed to get item from localStorage with key "${key}":`, error);
    return null;
  }
};

/**
 * Safely set an item in localStorage
 * @param key - The localStorage key
 * @param value - The value to store
 * @returns true if successful, false if failed
 */
export const setToLocalStorage = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`Failed to set item in localStorage with key "${key}":`, error);
    return false;
  }
};

/**
 * Safely remove an item from localStorage
 * @param key - The localStorage key
 * @returns true if successful, false if failed
 */
export const removeFromLocalStorage = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove item from localStorage with key "${key}":`, error);
    return false;
  }
};

/**
 * Check if localStorage is available
 * @returns true if localStorage is available, false otherwise
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Clear localStorage while preserving specific keys
 * @param keysToKeep - Array of keys to preserve in localStorage
 * @returns true if successful, false if failed
 */
export const clearLocalStorageExcept = (keysToKeep: string[]): boolean => {
  try {
    // Store the values of keys we want to keep
    const preservedValues: Record<string, string | null> = {};
    keysToKeep.forEach(key => {
      preservedValues[key] = localStorage.getItem(key);
    });

    // Clear all localStorage
    localStorage.clear();

    // Restore the preserved values
    Object.entries(preservedValues).forEach(([key, value]) => {
      if (value !== null) {
        localStorage.setItem(key, value);
      }
    });

    return true;
  } catch (error) {
    console.warn('Failed to clear localStorage while preserving keys:', error);
    return false;
  }
};
